import MainModalsContext from '@/contexts/main-modals.context'
import { IHMSDedicatedServer } from '@/types/dedicated.interface'
import { useRouter } from 'next/router'
import { FC, PropsWithChildren, useEffect, useMemo, useState } from 'react'
import dynamic from 'next/dynamic'

const LoginModal = dynamic(() => import('@/components/popups/LoginModal'), {
  ssr: false,
})

const BalanceModal = dynamic(() => import('@/components/popups/BalanceModal'), {
  ssr: false,
})

const OnSelectPlanModal = dynamic(() => import('@/components/popups/OnSelectPlanModal'), {
  ssr: false,
})

const OrderDedicatedServerModal = dynamic(
  () => import('@/components/popups/OrderDedicatedServerModal'),
  { ssr: false }
)

const SearchPopup = dynamic(() => import('@/components/popups/SearchPopup'), {
  ssr: false,
})

const OrderPopup = dynamic(() => import('@/components/popups/OrderPopup'), {
  ssr: false,
})

const MainModalsContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const router = useRouter()

  const [activeLoginModal, setActiveLoginModal] = useState<boolean>(false)
  const [activeBalanceModal, setActiveBalanceModal] = useState<boolean>(false)
  const [activeOnSelectPlanModal, setActiveOnSelectPlanModal] = useState<boolean>(false)
  const [activeSearchPopup, setActiveSearchPopup] = useState<boolean>(false)
  const [activeOrderDedicatedServerModal, setActiveOrderDedicatedServerModal] = useState<boolean>(false)
  const [dedicatedPlan, setDedicatedPlan] = useState<IHMSDedicatedServer>()

  const loginModal = useMemo(
    () => ({
      active: activeLoginModal,
      setActive: setActiveLoginModal,
    }),
    [activeLoginModal]
  )

  const balanceModal = useMemo(
    () => ({
      active: activeBalanceModal,
      setActive: setActiveBalanceModal,
    }),
    [activeBalanceModal]
  )

  const onSelectPlanModal = useMemo(
    () => ({
      active: activeOnSelectPlanModal,
      setActive: setActiveOnSelectPlanModal,
    }),
    [activeOnSelectPlanModal]
  )

  const searchPopup = useMemo(
    () => ({
      active: activeSearchPopup,
      setActive: setActiveSearchPopup,
    }),
    [activeSearchPopup]
  )

  const orderDedicatedServerModal = useMemo(
    () => ({
      active: activeOrderDedicatedServerModal,
      setActive: setActiveOrderDedicatedServerModal,
      plan: dedicatedPlan,
      setPlan: setDedicatedPlan,
    }),
    [activeOrderDedicatedServerModal, dedicatedPlan]
  )

  const value = useMemo(
    () => ({
      loginModal,
      balanceModal,
      onSelectPlanModal,
      searchPopup,
      orderDedicatedServerModal,
    }),
    [loginModal, balanceModal, onSelectPlanModal, searchPopup, orderDedicatedServerModal]
  )

  useEffect(() => {
    if (router.query.auth) {
      value.loginModal.setActive(true)
    } else {
      if (value.loginModal.active) {
        value.loginModal.setActive(false)
      }
    }
  }, [router.query.auth, value.loginModal.active])

  useEffect(() => {
    if (router.query.search) {
      value.searchPopup.setActive(true)
    }
  }, [router.query.search])

  return (
    <MainModalsContext.Provider value={value}>
      {children}
      <LoginModal active={activeLoginModal} onClose={() => value.loginModal.setActive(false)} />
      <BalanceModal
        active={activeBalanceModal}
        onClose={() => value.balanceModal.setActive(false)}
      />
      <OnSelectPlanModal
        open={activeOnSelectPlanModal}
        onClose={() => value.onSelectPlanModal.setActive(false)}
      />
      <SearchPopup open={activeSearchPopup} onClose={() => value.searchPopup.setActive(false)} />
      <OrderPopup />
      <OrderDedicatedServerModal
        open={activeOrderDedicatedServerModal}
        onClose={() => value.orderDedicatedServerModal.setActive(false)}
        plan={value.orderDedicatedServerModal.plan}
      />
    </MainModalsContext.Provider>
  )
}

export default MainModalsContextProvider
